import React, { useMemo, useRef, useState, useEffect } from 'react';
import { Select, SelectProps, Space, Spin } from 'antd';
import Checkbox from 'components/checkbox';
import { DateLabel, DateWrapper } from 'pages/Accounts/components/AccountsFilters/styles';
import { InputTypeSelectMultiple } from './styles';
import './styles.scss';
import { ReactComponent as TimesSvg } from 'assets/icons/times.svg';
import Tooltip from 'components/tooltip';
import SelectNotFoundElement from './selectNotFoundElement';
import Notification from 'components/notification';
import { IGetUsersParams } from 'contexts/certifications/types';
import { getModalPopupContainer } from 'utils/popupContainer';

interface InputSelectMultipleProps extends SelectProps {
  optionRender?: (option) => JSX.Element;
  customClassName?: string;
  values: string[];
  options?: {
    label: string;
    value: string;
    key: string | number;
  }[];
  inputLabel?: string;
  name?: string;
  maxDropdownHeight?: string;
  maxTagCount?: number;
  fetchOptionsFunc?: (data: IGetUsersParams) => void;
  fetching?: boolean;
  loadingMoreItems?: boolean;
  isAllSelectedOption?: (option) => boolean;
  customFilterOptions?: (value, option) => boolean;
  reseteable?: boolean;
  lazyLoadingEnabled?: boolean;
}

const InputSelectMultiple: React.FC<InputSelectMultipleProps> = ({
  optionRender,
  customClassName,
  values,
  inputLabel,
  name,
  options,
  maxTagCount,
  maxDropdownHeight,
  fetchOptionsFunc,
  fetching,
  loadingMoreItems,
  isAllSelectedOption,
  customFilterOptions,
  reseteable,
  lazyLoadingEnabled,
  ...rest
}) => {
  const { Option } = Select;
  const [searchQuery, setSearchQuery] = useState('');
  const [fetchingData, setFetchingData] = useState(false);
  const [currentOptions, setCurrentOptions] = useState(options);

  const tagsLength = maxTagCount ?? 1;
  const dropdownRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef(null);

  const getTooltipValues = () => {
    return values.slice(tagsLength).map((value, index) => {
      const option = options.find((option) => option.value === value);
      return <div key={index}>{option?.label}</div>;
    });
  };

  const CustomTagsPlaceholder = () => (
    <Tooltip placement="right" title={getTooltipValues()}>
      <div>+{values.length - tagsLength}...</div>
    </Tooltip>
  );

  const OptionsPills = (): JSX.Element => (
    <>
      {currentOptions.map((option) => (
        <Option
          key={option.key}
          value={option.value}
          label={option.label}
          className={customClassName ?? ''}
        >
          <Space>
            <Checkbox
              checked={
                values.includes(option.value) ||
                (values.length === 0 &&
                  currentOptions.length > 0 &&
                  isAllSelectedOption &&
                  isAllSelectedOption(option))
              }
              controlled
            />
            {optionRender ? optionRender(option) : <span>{option.label}</span>}
          </Space>
        </Option>
      ))}
      {loadingMoreItems && (
        <Option key={'loading'} disabled className={customClassName ?? ''}>
          <Space style={{ margin: 'auto' }}>
            <Spin size="small" />
          </Space>
        </Option>
      )}
    </>
  );

  const logCompletedInput = async (value) => {
    if (value !== searchQuery) {
      try {
        setCurrentOptions([]);
        setSearchQuery(value);
        if (fetchOptionsFunc) {
          await fetchOptionsFunc({ query: value });
        }
      } catch (error) {
        Notification({ text: 'There has been a problem fetching users', type: 'error' });
      }
    }
  };

  const handleSearch = (value) => {
    if (fetchOptionsFunc) {
      if (!fetchingData) setFetchingData(true);
      setCurrentOptions([]);
      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        logCompletedInput(value).finally(() => {
          setFetchingData(false);
        });
      }, 800);
    } else {
      const filteredOptions = options.filter((option) =>
        option.label.toLowerCase().includes(value.toLowerCase())
      );
      setCurrentOptions(filteredOptions);
    }
  };

  const handleSelect = () => {
    logCompletedInput('');
    if (reseteable) setCurrentOptions(options);
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (!lazyLoadingEnabled) return;
    const target = e.target as HTMLDivElement;
    if (!fetchingData && target.scrollTop + target.offsetHeight >= target.scrollHeight - 15) {
      if (!loadingMoreItems && fetchOptionsFunc) {
        fetchOptionsFunc({ load_more: true });
      }
    }
  };

  useEffect(() => {
    if (loadingMoreItems) {
      setTimeout(() => {
        const option = Array.from(
          dropdownRef.current?.querySelectorAll('.ant-select-item-option') || []
        )[currentOptions.length];

        if (option) {
          option.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
          });
        }
      }, 300);
    }
  }, [loadingMoreItems]);

  useEffect(() => {
    if (!fetching) {
      setCurrentOptions(options);
    }
  }, [options, fetching]);

  const loadingSpinner = (): JSX.Element => (
    <Spin size="small" style={{ margin: 'auto', width: '100%' }} />
  );

  const showDropdownSpinner = useMemo(() => {
    return !loadingMoreItems && (fetchingData || fetching);
  }, [fetchingData, fetching, loadingMoreItems]);

  return (
    <DateWrapper>
      <DateLabel>{inputLabel}</DateLabel>
      <InputTypeSelectMultiple
        clearable={true}
        clearIcon={<TimesSvg />}
        value={values}
        mode="multiple"
        maxTagCount={tagsLength}
        maxTagPlaceholder={<CustomTagsPlaceholder />}
        dropdownClassName={`form-select-multiple-options ${name}-dropdown-items-wrapper`}
        dropdownStyle={{ zIndex: 9999 }}
        getPopupContainer={getModalPopupContainer}
        dropdownMatchSelectWidth={false}
        optionLabelProp="label"
        filterOption={!customFilterOptions}
        onSearch={handleSearch}
        onSelect={handleSelect}
        notFoundContent={!showDropdownSpinner ? <SelectNotFoundElement /> : loadingSpinner()}
        onBlur={() => {
          logCompletedInput('');
          if (reseteable) setCurrentOptions(options);
        }}
        placement={'bottomLeft'}
        onPopupScroll={handleScroll}
        dropdownRender={(menu) => (
          <div
            ref={dropdownRef}
            onScroll={handleScroll}
            style={{
              maxHeight: maxDropdownHeight,
              overflowY: maxDropdownHeight ? 'auto' : undefined,
            }}
          >
            {menu}
          </div>
        )}
        {...rest}
      >
        {OptionsPills()}
      </InputTypeSelectMultiple>
    </DateWrapper>
  );
};

export default InputSelectMultiple;
